import { isEmpty } from "es-toolkit/compat";
import { useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useEffect, useMemo, useRef } from "react";

import { schema } from "@moment/prosemirror-schema";

import { $loggedInUser } from "../../internal/authenication";
import {
	$document,
	docsListCollabEntry,
	docsListItemLocalEntry,
	docsListItemRemoteEntry,
} from "../../internal/document-list";
import {
	PRIMARY_AGENTS_MD_PAGE_ID,
	PRIMARY_AGENT_INSTRUCTION_FILE,
	isAgentInstructionPage,
	isPrimaryAgentInstructionPage,
} from "../../internal/document/agents-md.atom";
import { env } from "../../internal/env";
import { $gitStatus } from "../../internal/git";
import { $editorState, $editorStateError } from "../../internal/prosemirror/editor-state.atom";
import { editorStateKeyForDocsRoute } from "../../internal/prosemirror/editorStateKey";
import { PmRebootLoadError } from "../../internal/prosemirror/errors";
import { $loadEditorStateFromDisk } from "../../internal/prosemirror/load-editor-state-from-disk.atom";
import { $loadEditorStateFromReboot } from "../../internal/prosemirror/load-editor-state-from-reboot.atom";
import { $pluginFactory } from "../../internal/prosemirror/plugins-factory.atom";

/**
 * For agent instruction files (AGENTS.md, CLAUDE.md) that live outside pagesMetadata,
 * this hook ensures the editor state is loaded when the page is viewed.
 *
 * In collab mode, for the primary agent file (AGENTS.md), it attempts to load
 * from Reboot first (collab-enabled). If Reboot fails (legacy doc without page
 * authority), it falls back to disk loading. For non-primary agent files
 * (CLAUDE.md) or non-collab mode, it loads from disk directly.
 *
 * Call this from the page content component alongside useEditorState.
 */
export const useAgentInstructionEditorState = (
	documentId: string,
	pageId: string,
	pagePath?: string
) => {
	const editorStateKey = useMemo(
		() => editorStateKeyForDocsRoute({ documentId, pageId }),
		[documentId, pageId]
	);

	const documentAtom = useMemo(() => $document(documentId), [documentId]);
	const documentLoadableAtom = useMemo(() => loadable(documentAtom), [documentAtom]);
	const documentLoadable = useAtomValue(documentLoadableAtom);
	const pluginFactory = useAtomValue($pluginFactory);
	const loadFromDisk = useSetAtom($loadEditorStateFromDisk);
	const loadFromReboot = useSetAtom($loadEditorStateFromReboot);
	const loggedInUser = useAtomValue($loggedInUser);

	const isAgentFile = isAgentInstructionPage(pagePath);
	const isPrimaryAgent = isPrimaryAgentInstructionPage(pagePath);
	const documentListItem =
		documentLoadable.state === "hasData" ? documentLoadable.data : undefined;
	const hasLocal = documentListItem ? !!docsListItemLocalEntry(documentListItem) : false;

	// Detect collab mode
	const gitStatusAtom = useMemo(() => $gitStatus(documentId), [documentId]);
	const gitStatusLoadable = useAtomValue(useMemo(() => loadable(gitStatusAtom), [gitStatusAtom]));
	const isOnCollabBranch =
		gitStatusLoadable.state === "hasData" && gitStatusLoadable.data?.branch === "collab/main";
	const remoteEntry = documentListItem ? docsListItemRemoteEntry(documentListItem) : undefined;
	const collabEntry = docsListCollabEntry(remoteEntry);
	const hasCollab = !isEmpty(collabEntry);
	const isCollabMode = hasCollab && isOnCollabBranch;
	const gitStatusResolved = gitStatusLoadable.state !== "loading";

	// Check editor state and error to determine load status
	const editorStateAtom = useMemo(() => $editorState(editorStateKey), [editorStateKey]);
	const editorStateValue = useAtomValue(editorStateAtom);
	const editorErrorAtom = useMemo(() => $editorStateError(editorStateKey), [editorStateKey]);
	const editorError = useAtomValue(editorErrorAtom);
	const hasRebootError = editorError instanceof PmRebootLoadError;

	// Track load attempts to avoid retriggering
	const rebootLoadTriggeredRef = useRef(false);
	const diskLoadTriggeredRef = useRef(false);
	useEffect(() => {
		rebootLoadTriggeredRef.current = false;
		diskLoadTriggeredRef.current = false;
	}, [editorStateKey]);

	// For primary AGENTS.md in collab mode: try Reboot first
	useEffect(() => {
		if (
			!isPrimaryAgent ||
			!isCollabMode ||
			!(env.isDesktopApp || env.isMobileApp) ||
			!documentListItem ||
			!hasLocal ||
			!pluginFactory ||
			!loggedInUser?.sub ||
			!collabEntry ||
			!remoteEntry?.workspace?.slug ||
			editorStateValue !== null ||
			rebootLoadTriggeredRef.current
		) {
			return;
		}

		rebootLoadTriggeredRef.current = true;

		const readonly = !(env.isDesktopApp || env.isMobileApp);
		void loadFromReboot({
			key: editorStateKey,
			workspaceSlug: remoteEntry.workspace.slug,
			documentId,
			pageId: PRIMARY_AGENTS_MD_PAGE_ID,
			branch: collabEntry.branch,
			sha: collabEntry.sha,
			pluginFactory,
			schema,
			readonly,
			localEntry: docsListItemLocalEntry(documentListItem),
			userId: loggedInUser.sub,
			pagePath: PRIMARY_AGENT_INSTRUCTION_FILE,
		});
	}, [
		isPrimaryAgent,
		isCollabMode,
		documentListItem,
		hasLocal,
		pluginFactory,
		loggedInUser?.sub,
		collabEntry,
		remoteEntry?.workspace?.slug,
		editorStateValue,
		editorStateKey,
		documentId,
		loadFromReboot,
	]);

	// Disk loading: for non-collab mode, OR as fallback after Reboot failure
	useEffect(() => {
		if (
			!isAgentFile ||
			!(env.isDesktopApp || env.isMobileApp) ||
			!documentListItem ||
			!hasLocal ||
			!pluginFactory ||
			editorStateValue !== null ||
			diskLoadTriggeredRef.current
		) {
			return;
		}

		// Wait for git status to resolve before deciding disk vs Reboot for primary AGENTS.md.
		// While git status is loading, isCollabMode is false even if the document actually has
		// collab — loading from disk now would race with the Reboot loader and preempt it.
		if (isPrimaryAgent && hasCollab && !gitStatusResolved) {
			return;
		}

		// In collab mode for primary AGENTS.md: only load from disk if Reboot failed
		if (isPrimaryAgent && isCollabMode && !hasRebootError) {
			return;
		}

		diskLoadTriggeredRef.current = true;

		const readonly = !(env.isDesktopApp || env.isMobileApp);
		const plugins = pluginFactory({
			documentId,
			pageId,
			schema,
			readonly,
			version: undefined,
		});

		void loadFromDisk({
			key: editorStateKey,
			document: documentListItem,
			pageId,
			pagePath,
			plugins,
		});
	}, [
		isAgentFile,
		isPrimaryAgent,
		isCollabMode,
		hasCollab,
		gitStatusResolved,
		hasRebootError,
		documentListItem,
		hasLocal,
		pluginFactory,
		editorStateValue,
		editorStateKey,
		documentId,
		pageId,
		pagePath,
		loadFromDisk,
	]);
};
